import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { PatientProfile } from "src/api/types";
import Spinner from "src/components/Spinner";
import WhiteCard from "src/components/WhiteCard";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import ShippingAddress from "src/components/ShippingAddress";
import { useManageModal } from "src/hooks/useManageModal";
import { formatPhoneNumber } from "src/utils/formatPhoneNumber";
import ProfileUpdateModal from "src/components/Modals/ProfileUpdateModal";
import PatientVitals from "src/components/PatientVitals";
import ContentCopy from "src/components/ContentCopy";
import TermsAndConditions from "src/components/TermsAndConditions";
import PriorAuthConsent from "src/components/PriorAuthConsent";
import PatientPreferredPharmacy from "src/components/PatientPreferredPharmacy";
import PatientRatings from "src/components/PatientRatings";
import StatusIndicator from "src/shared/StatusIndicator";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import ResetPatientPassword from "src/components/ResetPatientPassword";
import DosespotPatient from "src/components/DosespotPatient";

type PatientInformationTypes = {
  patient: PatientProfile | null;
  loading: boolean;
};

const PatientInformation = ({ patient, loading }: PatientInformationTypes) => {
  const [open, { handleClose, handleOpen }] = useManageModal();
  const isProvider = useClinician((c) =>
    c.roles.find((role) => ["Provider", "Lead Provider"].includes(role.name)),
  );

  if (!patient) {
    return <Typography>Could not find patient information</Typography>;
  }

  if (loading) {
    return <Spinner />;
  }

  return (
    <>
      <WhiteCard gap={1} sx={{ overflowY: "auto" }}>
        <Typography variant="subtitle2">PATIENT INFORMATION</Typography>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontWeight={600} sx={{ textDecoration: "underline" }}>
            Profile
          </Typography>
          <IconButton size="small" onClick={handleOpen}>
            <EditNoteOutlinedIcon fontSize="large" />
          </IconButton>
        </Stack>
        <Stack>
          <Stack
            gap={0}
            direction="row"
            justifyContent="space-between"
            alignItems={"center"}
          >
            <Typography>{`Id: ${patient.id}`}</Typography>
            <ContentCopy
              text={patient.id.toString()}
              contentType={"patientId"}
            />
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems={"center"}
          >
            <Typography>{`Email: ${patient.profile.email}`}</Typography>
            <ContentCopy text={patient.profile.email} contentType={"email"} />
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography>{`Phone number: ${formatPhoneNumber(
              patient.profile.phone_number ?? "",
            )}`}</Typography>
            <ContentCopy
              text={patient.profile.phone_number ?? ""}
              contentType={"phone number"}
            />
          </Stack>
          <Typography>{`Birth date: ${patient.profile.dob}`}</Typography>
          <Typography>{`Gender: ${patient.profile.gender}`}</Typography>
          <Typography>{`Preferred language: ${patient.profile.language.toUpperCase()}`}</Typography>
          <Typography>{`State: ${patient.profile.region}`}</Typography>
          <Typography>{`Status: ${patient.status}`}</Typography>
          {isProvider ? null : (
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
              <Typography>{`Communication Status:`}</Typography>
              <Tooltip
                title={
                  <Typography>
                    {patient.promotions_opt ? "subscribed" : "unsubscribed"}
                  </Typography>
                }
                placement="right"
                arrow
              >
                <span>
                  <StatusIndicator status={patient.promotions_opt} />
                </span>
              </Tooltip>
            </Stack>
          )}
        </Stack>
        <PatientRatings patientId={patient.id} />
        <DosespotPatient patient={patient} />
        <TermsAndConditions />
        <PriorAuthConsent patient={patient} />
        <ShippingAddress patient={patient} />
        <PatientPreferredPharmacy patientId={patient.id} />
        <PatientVitals patientId={patient.id} />
        <Stack alignSelf={"end"}>
          <ResetPatientPassword />
        </Stack>
      </WhiteCard>
      <ProfileUpdateModal open={open} onClose={handleClose} patient={patient} />
    </>
  );
};

export default PatientInformation;
